import React, { useContext, useState, useEffect, useCallback } from 'react'
import { s3Static } from '../../utils/constants'
import { UserContext } from '../../context/UserContext'
import { RateService } from './RateService'
import { openModal } from '../../service/modal-service'
import { downloadRating } from '../../api/user/profile-api'
import { SystemError } from '../error/SystemError'
import { LoadingCover } from '../commons/LoadingCover'
import { useRouter } from 'next/router'
import Image from 'next/image'
import { PullToRefresh } from '@/commons/PullToRefresh'
import { InfiniteScroll } from '@/commons/InfiniteScroll'
import { motion } from 'framer-motion'
import { Star, Flag, Loader2, MessageSquare, Briefcase } from 'lucide-react'
import { formatWhatsAppTime } from 'utils/time-converter'
import Link from 'next/link'

export const Review = (props) => {
    const about = props.about
    const router = useRouter()
    
    const stringHandleName = props.stringHandleName
    const stringsUserId = props.stringsUserId
    const profilePicture = props.profilePicture

    const [ pageNumber , setPageNumber ] = useState(0)
    const pageSize = 20
    const [ reviews, setReviews ] = useState([])
    const [ isShowMore, setIsShowMore ] = useState(true)
    const [ isLoadingReviews, setIsLoadingReviews ] = useState(false)

    const [ isShowRateService, setIsShowRateService ] = useState(false)

    const [ isLoading, setIsLoading ] = useState(false)
    const [ isSystemError, setIsSystemError ] = useState(false)
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""})

    const { userId } = useContext(UserContext)

    const reportReview = () => {
        setErrorContent({'header': "Report Review", 'isInfo': true, 'body': "To report a review please contact us at support@stringssg.com"});
        setIsSystemError(true);
    }

    const downloadReviews = useCallback(async() =>{    
        try {
            const response = await downloadRating(0, stringsUserId, pageNumber, pageSize)
            setIsLoadingReviews(false);
            if (pageNumber === 0) {
                setReviews(response);
                if (response.length < pageSize) {
                    setIsShowMore(false);
                }
            } else {
                let newItemCount = response.length;
                setReviews(reviews => [...reviews, ...response]);
                if (newItemCount < pageSize) {
                    setIsShowMore(false);
                }
            }
        } catch (error) {
            console.log(error);
            setErrorContent({ 'header': "Error getting reviews", 'body': "Please try again. If problem persists, contact us at support@stringssg.com" });
            setIsSystemError(true);
            setIsLoadingReviews(false);
            return
        }
    }, [stringsUserId, pageNumber, pageSize])

    const refresh = useCallback(async() => {
        return new Promise((resolve) => {
            setIsShowMore(true)
            setIsLoadingReviews(true); 
            if(pageNumber === 0){
                downloadReviews()
            } else {
                setPageNumber(0)                
            }            
            resolve("success");
        })
    }, [pageNumber, downloadReviews])

    const loadMore = async() =>{
        return new Promise((resolve) => {            
            if(reviews.length > 0){
                setIsLoadingReviews(true); 
                setPageNumber(pageNumber + pageSize);
            }              
            resolve("success");
        });
    }

    useEffect(() => {     
        if(pageNumber === 0) return;
        downloadReviews();
    }, [pageNumber, downloadReviews])    

    useEffect(() => {
        if(!stringsUserId) return
        if(pageNumber > 0 || reviews.length > 0 || !isShowMore) return
        downloadReviews();
    }, [stringsUserId, pageNumber, downloadReviews, reviews, isShowMore])

    const changeRoute = (path) =>{
        setIsLoading(true)
        router.push(path).then(() => {
            setIsLoading(false)
        })
    }
    
    const rateService = () => {
        if(!userId){
            changeRoute("/signin?redirect=" + router.asPath)
            return
        }
        openModal(setIsShowRateService)
    }

    useEffect(() => {
        if(!stringsUserId) return
        setReviews(props.reviews)
    }, [stringsUserId, props.reviews])

    const getRatingColor = (strength) => {
        const colors = {
            success: 'bg-green-500',
            primary: 'bg-blue-500',
            info: 'bg-cyan-500',
            warning: 'bg-amber-500',
            danger: 'bg-red-500',
            default: 'bg-gray-400'
        }
        return colors[strength] || colors.default
    }

  return (
    <div className='bg-gray-50 min-h-screen'>
        {/* Header */}
        <div className='p-3 bg-white border-b border-gray-100'>
            <h1 className='text-lg font-semibold text-gray-900'>Reviews</h1>
        </div>

        <div className='pb-24' id="reviews">
            <PullToRefresh
                onRefresh={refresh}
                pullingContent={<div className='text-sm text-gray-400 text-center py-2'>↓ Pull to refresh</div>}
                pullDownThreshold={90}
                maxPullDownDistance={200}
            >
                {/* Profile Summary Card */}
                <div className='p-4'>
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className='bg-white rounded-2xl shadow-sm border border-gray-100 p-4'
                    >
                        <div className='flex items-center gap-4'>
                            <Link href={`/profile/${stringHandleName}`} 
                                className='relative w-16 h-16 shrink-0 '
                            >
                                {profilePicture && (
                                    <Image 
                                        className='rounded-xl'
                                        src={profilePicture}
                                        fill
                                        style={{objectFit: 'cover'}}
                                        placeholder='blur'
                                        blurDataURL={s3Static + "placeholder.png"}
                                        alt='profile image'
                                    />
                                )}
                            </Link>
                            <div className='flex-1 min-w-0'>
                                <h2 className='font-semibold text-gray-900 cursor-pointer hover:text-orange-600 transition-colors'>
                                    <Link href={`/profile/${stringHandleName}`}>
                                        {about.userFirstName} {about.userLastName}
                                    </Link>
                                </h2>
                                <div className='flex items-center gap-2 mt-1'>
                                    <span className={`text-white text-xs px-2 py-0.5 rounded-full font-medium ${getRatingColor(about.ratingStrength)}`}>
                                        {about.rating?.toFixed(1)}
                                    </span>
                                    <div className="flex gap-0.5">
                                        {[1,2,3,4,5].map((star) => (
                                            <Star 
                                                key={star} 
                                                size={14} 
                                                className={`${star <= Math.round(about.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                                {about.userAccountType === "Professional" && (
                                    <div className='flex items-center gap-1 mt-1'>
                                        <Briefcase size={12} className='text-gray-400' />
                                        <span className='text-xs text-gray-500'>{about.category}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Reviews Header */}
                <div className='px-4 flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                        <MessageSquare size={18} className='text-gray-400' />
                        <h3 className='font-medium text-gray-700'>
                            {reviews?.length || 0} Reviews
                        </h3>
                    </div>
                    <div className='flex items-center gap-2'>
                        {(reviews?.length > 0 && userId === stringsUserId) && (
                            <button 
                                onClick={reportReview} 
                                className='flex items-center gap-1 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg transition-colors'
                            >
                                <Flag size={12} />
                                Report
                            </button>
                        )}
                        {userId != stringsUserId && (
                            <motion.button 
                                whileTap={{ scale: 0.95 }}
                                onClick={rateService}
                                className='flex items-center gap-1 px-4 py-2 bg-linear-to-r from-orange-500 to-amber-500 text-white text-xs font-medium rounded-xl shadow-sm'
                            >
                                <Star size={14} />
                                Rate Service
                            </motion.button>
                        )}
                    </div>
                </div>

                {/* Loading State */}
                {isLoadingReviews && reviews.length === 0 && (
                    <div className='flex justify-center py-8'>
                        <Loader2 size={24} className='text-orange-500 animate-spin' />
                    </div>
                )}

                {/* Reviews List */}
                <InfiniteScroll
                    loadMore={loadMore}
                    hasMore={isShowMore && !isLoadingReviews}
                    loader={
                        <div className='flex justify-center py-4'>
                            <Loader2 size={20} className='text-orange-500 animate-spin' />
                        </div>
                    }
                >
                    <div className='px-4 py-3 space-y-3'>
                        {reviews?.length === 0 && !isLoadingReviews && (
                            <div className='text-center py-12'>
                                <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center'>
                                    <MessageSquare size={24} className='text-gray-400' />
                                </div>
                                <p className='text-gray-500'>No reviews yet</p>
                                {userId != stringsUserId && (
                                    <p className='text-sm text-gray-400 mt-1'>Be the first to leave a review!</p>
                                )}
                            </div>
                        )}
                        
                        {[...reviews].map((review, i) => (
                            <motion.div 
                                key={i}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.05 }}
                                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4"
                            >
                                <div className='flex items-start justify-between'>
                                    <div>
                                        <h4 className='font-medium text-gray-900'>{review.requestUserFullNm}</h4>
                                        <div className='flex items-center gap-2 mt-1'>
                                            <div className="flex gap-0.5">
                                                {[1,2,3,4,5].map((star) => (
                                                    <Star 
                                                        key={star} 
                                                        size={12} 
                                                        className={`${star <= Math.round(review.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                                                    />
                                                ))}
                                            </div>
                                            <span className={`text-white text-xs px-1.5 py-0.5 rounded font-medium ${getRatingColor(review.ratingStrength)}`}>
                                                {review.rating}
                                            </span>
                                        </div>
                                    </div>
                                    <span className='text-xs text-gray-400'>{formatWhatsAppTime(review.reviewTime)}</span>
                                </div>
                                {review.reviewComment && (
                                    <p className='text-sm text-gray-600 mt-3 leading-relaxed'>
                                        {review.reviewComment}
                                    </p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </InfiniteScroll>
            </PullToRefresh>
        </div>  
        
        {isShowRateService && (
            <RateService stringsUserId={stringsUserId} about={about} downloadReviews={downloadReviews} />
        )}  
        {isLoading && <LoadingCover/>}
        {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
    </div>
  )
}
